import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminHeader from "../AdminHeader";
import ErrorMessage from "../../ErrorMessage";
import GamesTable from "../GamesTable";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import "./AdminDashboard.scss";

const AdminDashboard = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { client, getAuthHeaders, handleAuthError, requireAuth, logout } =
    useAdminAuth();

  useEffect(() => {
    if (!requireAuth()) return;
  }, [requireAuth]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "/api/games?admin=true",
        getAuthHeaders()
      );
      setGames(response.data);
    } catch (error) {
      if (!handleAuthError(error)) {
        setError(error.response?.data?.error || "Failed to fetch games");
      }
    } finally {
      setLoading(false);
    }
  };

  // Optimistic update handlers
  const deleteGame = async (gameId) => {
    if (!window.confirm("Are you sure you want to delete this game?")) {
      return;
    }

    // Optimistically remove from UI
    const originalGames = games;
    setGames(games.filter((game) => game.id !== gameId));

    try {
      await axios.delete(`/api/games?id=${gameId}`, getAuthHeaders());
    } catch (error) {
      // Restore original state on error
      setGames(originalGames);
      if (!handleAuthError(error)) {
        setError(error.response?.data?.error || "Failed to delete game");
      }
    }
  };

  const toggleActive = async (game) => {
    const hasSchedule = Boolean(game.activeFrom || game.activeUntil);
    let clearSchedule = false;
    if (hasSchedule) {
      const confirmed = window.confirm(
        "This game has a schedule. Override and clear the scheduled times?"
      );
      if (!confirmed) return;
      clearSchedule = true;
    }

    // Optimistically update in UI
    const originalGames = games;
    setGames(
      games.map((g) =>
        g.id === game.id
          ? {
              ...g,
              isActive: !g.isActive,
              ...(clearSchedule ? { activeFrom: null, activeUntil: null } : {}),
            }
          : g
      )
    );

    try {
      await axios.put(
        "/api/games",
        {
          ...game,
          isActive: !game.isActive,
          ...(clearSchedule ? { activeFrom: null, activeUntil: null } : {}),
        },
        getAuthHeaders()
      );
    } catch (error) {
      // Restore original state on error
      setGames(originalGames);
      if (!handleAuthError(error)) {
        setError(error.response?.data?.error || "Failed to update game");
      }
    }
  };

  const duplicateGame = async (game) => {
    if (!window.confirm(`Are you sure you want to duplicate "${game.name}"?`)) {
      return;
    }

    try {
      const response = await axios.post(
        "/api/games/duplicate",
        { gameId: game.id },
        getAuthHeaders()
      );

      // Add the new game to the list
      setGames([response.data, ...games]);
    } catch (error) {
      if (!handleAuthError(error)) {
        setError(error.response?.data?.error || "Failed to duplicate game");
      }
    }
  };

  useEffect(() => {
    if (client) {
      fetchGames();
    }
  }, [client]);

  const handleCreateGame = () => {
    navigate("/admin/games/create");
  };

  const handleEditGame = (game) => {
    navigate(`/admin/games/edit/${game.id}`);
  };

  const handleGoHome = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <AdminHeader client={client} onGoHome={handleGoHome} onLogout={logout} />

      <div className="admin-dashboard__content">
        <ErrorMessage error={error} onClose={() => setError("")} />

        <GamesTable
          games={games}
          onCreateGame={handleCreateGame}
          onEditGame={handleEditGame}
          onDuplicateGame={duplicateGame}
          onToggleActive={toggleActive}
          onDeleteGame={deleteGame}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
